import { type ReactElement } from 'react'
import { isEmpty, isTeamId, type EntityId, type PermissionType, type WithEntityId } from '@terros/common'
import { Alert, Stack, type MantineBreakpoint } from '@mantine/core'
import { useContainerBreakpoint } from '@hooks'
import { Size, usePermissionCheck, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { TAlert } from '../terros'
import { toPreferenceGroup } from './util'
import { PreferenceSearchInput } from './PreferenceSearchInput'
import { PreferenceGroupContent } from './PreferenceGroupContent'
import { PreferenceCategorySidebar } from './PreferenceCategorySidebar'
import { PreferenceCategoryAccordion } from './PreferenceCategoryAccordion'
import { PreferenceFormProvider, useAvailablePreferenceGroups, usePreferenceContext } from './Context'

const SIDEBAR_BREAKPOINT: MantineBreakpoint = 'xs'

export type PreferencesFormProps = WithEntityId & {
  readOnly?: boolean
}

export const PreferencesForm = ({ entityId: targetId, readOnly = false }: PreferencesFormProps): ReactElement => {
  const { data: isAllowedUpdate, isLoading: isCheckingPermission } = usePermissionCheck({
    targetId,
    permissionType: getPermissionType(targetId),
  })
  const { t } = useTranslation()

  return (
    <PreferenceFormProvider allowUpdate={Boolean(isAllowedUpdate) && !readOnly}>
      <Stack gap={Size.space['4xl']}>
        {!isAllowedUpdate && !isCheckingPermission && (
          <Alert color='red' icon={<MaterialSymbolIcon icon='warning' />}>
            {t('You do not have permission to update preferences.')}
          </Alert>
        )}
        <PreferenceSearchInput />
        <FormContent targetId={targetId} />
      </Stack>
    </PreferenceFormProvider>
  )
}

const FormContent = ({ targetId }: { targetId: EntityId }): ReactElement => {
  const { t } = useTranslation()
  const { filter, selectedGroup, setSelectedGroup } = usePreferenceContext()
  const availableGroups = useAvailablePreferenceGroups(filter)
  const { ref, isAtLeast: showSidebar } = useContainerBreakpoint(SIDEBAR_BREAKPOINT)

  const renderGroups = (): ReactElement => {
    if (isEmpty(availableGroups)) return <TAlert variant='warning'>{t('No preferences found for this search')}</TAlert>
    if (showSidebar) {
      return (
        <PreferenceCategorySidebar
          categories={availableGroups}
          currentGroup={selectedGroup}
          onSelect={setSelectedGroup}
        >
          <PreferenceGroupContent targetId={targetId} />
        </PreferenceCategorySidebar>
      )
    }
    return (
      <PreferenceCategoryAccordion
        categories={availableGroups}
        openGroup={toPreferenceGroup(selectedGroup) ?? availableGroups[0] ?? null}
        onChange={(category) => setSelectedGroup(toPreferenceGroup(category) ?? selectedGroup)}
      >
        <PreferenceGroupContent targetId={targetId} />
      </PreferenceCategoryAccordion>
    )
  }

  return (
    <Stack ref={ref} w='100%'>
      {renderGroups()}
    </Stack>
  )
}

const getPermissionType = (targetId: EntityId): PermissionType => {
  if (isTeamId(targetId)) return 'team:update'
  return 'company:update'
}
